import { useGetMachineAgentId } from '#~/agent/components/bridge/hooks.js';
import { useAgents } from '#~/agent/hooks.js';
import { fieldKeyedAgentsEqual, idNamePick } from '#~/agent/provider/use-agents-field-eq.js';
import { PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';
import * as Select from '@studio/design-system/select';
import { Spinner } from '@studio/design-system/spinner';
import type { Agent } from '@studio/models/agent';
import { useLatestFn } from '@studio/react-utils/hooks';
import { useEffect, useMemo } from 'react';
import { twMerge } from 'cn';
import { useProducer, useStore } from '../store/hooks.js';

export interface BridgeSelectorProps {
  className?: string;
}

export const BridgeSelector = ({ className }: BridgeSelectorProps) => {
  const { enabled: isBridgeEnabled } = useFeatureFlag(PostHogFeatureFlag.BRIDGE_FEATURE, {
    defaultValue: false
  });

  const producer = useProducer();
  const selectedAgentId = useStore((µ) => µ.agentId);
  const sourceSelection = useStore((µ) => µ.source.selection);
  const { agentId: machineAgentId, loading } = useGetMachineAgentId();

  const agents = useAgents(
    (a) => a.filter((a) => !a.embedded).map((a) => ({ id: a.id, name: a.name })),
    (left, right) => fieldKeyedAgentsEqual(left, right, idNamePick)
  );

  const availableAgents = useMemo(() => {
    return agents.filter((agent) => sourceSelection?.agentIds?.includes(agent.id));
  }, [agents, sourceSelection]);

  const safeSelectedAgentId = useMemo(() => {
    return availableAgents.find((a) => a.id === selectedAgentId)?.id || availableAgents[0]?.id;
  }, [availableAgents, selectedAgentId]);

  useEffect(() => {
    if (machineAgentId && !loading) {
      onSelectBridge(machineAgentId);
    }
  }, [machineAgentId, loading]);

  const onSelectBridge = useLatestFn(async (nextId: Agent['id']): Promise<void> => {
    let selectedDeviceID: string | undefined;
    producer((draft) => {
      draft.agentId = nextId;
      if (!draft.source.selection) return;
      selectedDeviceID = draft.source.preferredId;
    });

    await new Promise((resolve) => setTimeout(resolve, 250));

    producer((draft) => {
      // @ts-ignore
      draft.source.preferredId = selectedDeviceID;
    });
  });

  // Hide bridge selector if feature flag is disabled
  if (!isBridgeEnabled) {
    return null;
  }

  if (!sourceSelection || loading) {
    return loading ? <Spinner /> : null;
  }

  return (
    <div className={twMerge('mb-2', className)}>
      <p
        className={twMerge(
          'mt-12 font-medium',
          availableAgents.length === 1 ? 'text-muted' : 'text-light'
        )}
      >
        Bridge Selection
      </p>
      <p
        className={twMerge(
          'mb-12 text-sm font-regular',
          availableAgents.length === 1 ? 'text-muted' : ''
        )}
      >
        This is the Bridge that will be processing audio for this device.
      </p>
      <Select.Root
        onValueChange={onSelectBridge}
        value={safeSelectedAgentId}
        disabled={availableAgents.length === 1 || loading}
      >
        <Select.Trigger className='bg-base-100'>
          <Select.Value placeholder='Select a bridge' />
        </Select.Trigger>
        <Select.Content>
          {availableAgents.map((agent) => (
            <Select.Item key={agent.id} value={agent.id} className='text-muted'>
              {agent.name}
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Root>
    </div>
  );
};
